import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';
import '../styles/AdminMacroGame.css';

const GAME_NAME = 'Liquidity Crisis';
const GAME_ICON = '🌀';
const GAME_DESCRIPTION = 'Wind tunnel money grab - 3 players enter, grab as much cash as possible!';

const PAYOUTS = [
  { place: '1st (Most Money)', amount: 15000, perPerson: true },
  { place: '2nd', amount: 8000, perPerson: true },
  { place: '3rd', amount: 4000, perPerson: true },
  { place: '4th-6th', amount: 1000, perPerson: true },
  { place: '7th-9th', amount: 0, perPerson: true },
  { place: 'Last Place', amount: -3000, perPerson: true }
];

const AdminMoneyTunnel = () => {
  const [gameActive, setGameActive] = useState(false);
  const [tradingPaused, setTradingPaused] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const [groups, setGroups] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [timer, setTimer] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);

  const getAdminToken = () => localStorage.getItem('adminKey') || '';

  // Socket listener
  useEffect(() => {
    const handleMarketUpdate = (data) => {
      setTradingPaused(!data.tradingEnabled);
    };

    socket.on('market-state-update', handleMarketUpdate);

    return () => {
      socket.off('market-state-update', handleMarketUpdate);
    };
  }, []);

  // Fetch market state on mount
  useEffect(() => {
    const fetchMarketState = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/market/status`);
        setTradingPaused(!response.data.tradingEnabled);
      } catch (error) {
        console.error('Failed to fetch market state:', error);
      }
    };
    fetchMarketState();
  }, []);

  // Timer countdown
  useEffect(() => {
    let interval;
    if (timerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setTimerRunning(false);
      // Broadcast timer end
      socket.emit('moneytunnel:timer-end', { round: currentRound });
    }
    return () => clearInterval(interval);
  }, [timerRunning, timer, currentRound]);

  const startGame = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/big-game/announce`, {
        gameName: GAME_NAME,
        announcement: `${GAME_ICON} ${GAME_NAME} is starting! Trading is paused. Get ready to grab some cash!`,
        pauseTrading: true
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      // Broadcast to sideboards
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'moneytunnel:start'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      setGameActive(true);
      setCurrentRound(0);
      setTradingPaused(true);
      setMessage({ text: 'Game started! Trading paused.', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    }
  };

  const endGame = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/trading/resume`, {}, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      await axios.post(`${API_BASE_URL}/api/admin/big-game/announce`, {
        gameName: `${GAME_NAME} - END`,
        announcement: `${GAME_ICON} ${GAME_NAME} has concluded! Trading resumes.`,
        pauseTrading: false
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      // Broadcast to sideboards
      await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
        type: 'moneytunnel:end'
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      setGameActive(false);
      setCurrentRound(0);
      setTradingPaused(false);
      setGroups([]);
      setMessage({ text: 'Game ended! Trading resumed.', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error: ${error.message}`, type: 'error' });
    }
  };

  const addGroup = () => {
    const groupNumber = groups.length + 1;
    setGroups([...groups, {
      id: groupNumber,
      players: [
        { name: `Player ${(groupNumber - 1) * 3 + 1}`, amount: 0 },
        { name: `Player ${(groupNumber - 1) * 3 + 2}`, amount: 0 },
        { name: `Player ${(groupNumber - 1) * 3 + 3}`, amount: 0 }
      ],
      completed: false
    }]);
  };

  const startRound = async (groupIndex) => {
    setCurrentRound(groupIndex + 1);
    setTimer(60); // 60 seconds per round
    setTimerRunning(true);

    // Broadcast to sideboards
    await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
      type: 'moneytunnel:round',
      round: groupIndex + 1,
      players: groups[groupIndex].players.map(p => p.name)
    }, {
      headers: { 'x-admin-token': getAdminToken() }
    });

    setMessage({ text: `Round ${groupIndex + 1} started! 60 seconds on the clock!`, type: 'success' });
  };

  const stopRound = () => {
    setTimerRunning(false);
    setTimer(null);

    // Mark current group as completed
    const updated = groups.map((g, i) =>
      i === currentRound - 1 ? { ...g, completed: true } : g
    );
    setGroups(updated);

    setMessage({ text: `Round ${currentRound} complete!`, type: 'success' });
  };

  const updatePlayerAmount = (groupIndex, playerIndex, amount) => {
    const updated = groups.map((g, gi) => {
      if (gi === groupIndex) {
        const newPlayers = g.players.map((p, pi) =>
          pi === playerIndex ? { ...p, amount: parseInt(amount) || 0 } : p
        );
        return { ...g, players: newPlayers };
      }
      return g;
    });
    setGroups(updated);
  };

  const updatePlayerName = (groupIndex, playerIndex, name) => {
    const updated = groups.map((g, gi) => {
      if (gi === groupIndex) {
        const newPlayers = g.players.map((p, pi) =>
          pi === playerIndex ? { ...p, name } : p
        );
        return { ...g, players: newPlayers };
      }
      return g;
    });
    setGroups(updated);
  };

  const announceWinner = async (playerName, amount) => {
    await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
      type: 'moneytunnel:winner',
      winner: playerName,
      amount: amount
    }, {
      headers: { 'x-admin-token': getAdminToken() }
    });

    setMessage({ text: `${playerName} announced as winner with $${amount}!`, type: 'success' });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAllPlayersSorted = () => {
    const allPlayers = groups.flatMap(g => g.players);
    return allPlayers.sort((a, b) => b.amount - a.amount);
  };

  const generatePayoutQR = async (amount, reason) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/generate-qr`, {
        type: amount >= 0 ? 'bonus' : 'penalty',
        amount: amount,
        reason: `${GAME_NAME}: ${reason}`
      }, {
        headers: { 'x-admin-token': getAdminToken() }
      });

      window.open(`/qr/${response.data.qrCode}`, '_blank', 'width=400,height=500');
      setMessage({ text: `QR generated: ${reason}`, type: 'success' });
    } catch (error) {
      setMessage({ text: `Error generating QR: ${error.message}`, type: 'error' });
    }
  };

  return (
    <div className="admin-macro-game money-tunnel">
      <div className="game-header">
        <h2>{GAME_ICON} {GAME_NAME}</h2>
        <p className="game-description">{GAME_DESCRIPTION}</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Game Status */}
      <div className="status-bar">
        <div className={`status-item ${gameActive ? 'active' : 'inactive'}`}>
          <span className="status-label">Game:</span>
          <span className="status-value">{gameActive ? 'ACTIVE' : 'NOT STARTED'}</span>
        </div>
        <div className={`status-item ${tradingPaused ? 'paused' : 'active'}`}>
          <span className="status-label">Trading:</span>
          <span className="status-value">{tradingPaused ? 'PAUSED' : 'ACTIVE'}</span>
        </div>
        {currentRound > 0 && (
          <div className="status-item">
            <span className="status-label">Round:</span>
            <span className="status-value">{currentRound}/{groups.length}</span>
          </div>
        )}
        {timer !== null && (
          <div className={`status-item timer ${timer <= 10 ? 'warning' : ''}`}>
            <span className="status-label">Time:</span>
            <span className="status-value">{formatTime(timer)}</span>
          </div>
        )}
      </div>

      {/* Main Controls */}
      <div className="control-section">
        <h3>Game Controls</h3>
        <div className="button-row">
          {!gameActive ? (
            <button className="btn primary large" onClick={startGame}>
              🚀 Start {GAME_NAME}
            </button>
          ) : (
            <button className="btn danger large" onClick={endGame}>
              🛑 End Game
            </button>
          )}
        </div>
      </div>

      {/* Groups of 3 */}
      {gameActive && (
        <div className="groups-section">
          <h3>Groups of 3 ({groups.length} groups, {groups.length * 3} players)</h3>
          <button className="btn" onClick={addGroup} disabled={groups.length >= 10}>
            ➕ Add Group of 3
          </button>

          <div className="groups-grid">
            {groups.map((group, groupIndex) => (
              <div
                key={group.id}
                className={`group-card ${group.completed ? 'completed' : ''} ${currentRound === groupIndex + 1 && timerRunning ? 'active' : ''}`}
              >
                <div className="group-header">
                  <span className="group-number">Group {group.id}</span>
                  {group.completed && <span className="completed-badge">✓ Done</span>}
                  {currentRound === groupIndex + 1 && timerRunning && (
                    <span className="active-badge">🔴 LIVE</span>
                  )}
                </div>

                <div className="players-list">
                  {group.players.map((player, playerIndex) => (
                    <div key={playerIndex} className="player-row">
                      <input
                        type="text"
                        value={player.name}
                        onChange={(e) => updatePlayerName(groupIndex, playerIndex, e.target.value)}
                        placeholder={`Player ${playerIndex + 1}`}
                        className="player-name-input"
                      />
                      <div className="amount-input-wrapper">
                        <span className="dollar-sign">$</span>
                        <input
                          type="number"
                          value={player.amount || ''}
                          onChange={(e) => updatePlayerAmount(groupIndex, playerIndex, e.target.value)}
                          placeholder="0"
                          className="amount-input"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="group-actions">
                  {!group.completed && !timerRunning && (
                    <button
                      className="btn primary"
                      onClick={() => startRound(groupIndex)}
                    >
                      ▶️ Start Round
                    </button>
                  )}
                  {currentRound === groupIndex + 1 && timerRunning && (
                    <button className="btn danger" onClick={stopRound}>
                      ⏹️ Stop Round
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {groups.length > 0 && groups.some(g => g.completed) && (
        <div className="leaderboard-section">
          <h3>💰 Leaderboard</h3>
          <div className="leaderboard">
            {getAllPlayersSorted().map((player, index) => (
              <div
                key={index}
                className={`leaderboard-row ${index === 0 ? 'first' : ''} ${index === 1 ? 'second' : ''} ${index === 2 ? 'third' : ''}`}
              >
                <span className="rank">#{index + 1}</span>
                <span className="player-name">{player.name}</span>
                <span className="player-amount">${player.amount.toLocaleString()}</span>
                {index === 0 && (
                  <button
                    className="btn small gold"
                    onClick={() => announceWinner(player.name, player.amount)}
                  >
                    🏆 Announce
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Payout Buttons */}
      <div className="payout-section">
        <h3>Quick Payout QR Codes</h3>
        <p className="payout-note">Generate QR codes for players to scan based on ranking</p>
        <div className="payout-grid">
          {PAYOUTS.map(payout => (
            <button
              key={payout.place}
              className={`payout-btn ${payout.amount >= 0 ? 'bonus' : 'penalty'}`}
              onClick={() => generatePayoutQR(payout.amount, payout.place)}
            >
              <span className="place">{payout.place}</span>
              <span className="amount">
                {payout.amount >= 0 ? '+' : ''}{payout.amount.toLocaleString()}
              </span>
            </button>
          ))}
        </div>
        <div className="special-bonuses">
          <h4>Special Bonuses</h4>
          <div className="button-row">
            <button className="btn bonus" onClick={() => generatePayoutQR(5000, 'Money Machine')}>
              Money Machine +$5K
            </button>
            <button className="btn bonus" onClick={() => generatePayoutQR(2000, 'Quick Hands')}>
              Quick Hands +$2K
            </button>
            <button className="btn bonus" onClick={() => generatePayoutQR(1000, 'Good Sport')}>
              Good Sport +$1K
            </button>
          </div>
        </div>

        <div className="sideboard-animations">
          <h4>Sideboard Animations</h4>
          <p className="animation-note">Trigger visual effects on LED sideboards</p>
          <div className="button-row">
            <button className="btn" onClick={async () => {
              await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
                type: 'market:money-rain'
              }, { headers: { 'x-admin-token': getAdminToken() } });
              setMessage({ text: 'Money rain animation triggered!', type: 'success' });
            }}>
              💵 Money Rain
            </button>
            <button className="btn gold" onClick={async () => {
              await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
                type: 'market:explosion', color: 'gold'
              }, { headers: { 'x-admin-token': getAdminToken() } });
              setMessage({ text: 'Gold explosion triggered!', type: 'success' });
            }}>
              💥 Gold Explosion
            </button>
            <button className="btn bonus" onClick={async () => {
              await axios.post(`${API_BASE_URL}/api/admin/events/broadcast`, {
                type: 'market:bull-run'
              }, { headers: { 'x-admin-token': getAdminToken() } });
              setMessage({ text: 'Bull run animation triggered!', type: 'success' });
            }}>
              📈 Bull Run
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h3>How to Run {GAME_NAME}</h3>
        <ol>
          <li><strong>Start game</strong> - This pauses trading and announces to all players</li>
          <li><strong>Add groups</strong> - Create groups of 3 players each</li>
          <li><strong>Enter player names</strong> - Fill in who's in each group</li>
          <li><strong>Start round</strong> - Click to start 60-second timer for that group</li>
          <li><strong>Players enter tunnel</strong> - 3 players enter, money is dumped on them</li>
          <li><strong>Grab money!</strong> - Players grab as much as they can in 60 seconds</li>
          <li><strong>Count & record</strong> - Count each player's haul and enter amounts</li>
          <li><strong>Stop round</strong> - Mark group as completed</li>
          <li><strong>Repeat</strong> - Do all groups</li>
          <li><strong>Announce winner</strong> - Player with most money wins!</li>
          <li><strong>Payout</strong> - Generate QR codes for rankings</li>
          <li><strong>End game</strong> - This resumes trading</li>
        </ol>
      </div>
    </div>
  );
};

export default AdminMoneyTunnel;
